/**
 * CreditNoteDialog (#1037)
 *
 * Issue a full or partial credit note against a finalized invoice. The user
 * picks which original lines to credit (all selected by default); the credit's
 * netto/ALV/brutto follow the selected lines. The original invoice is never
 * modified — the server creates a linked negative credit note.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displayLineGrossCents } from '@/utils/money';
import {
  DialogContent, Stack, Typography, Box, Divider, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Checkbox,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { FormDialog } from '@/components/common/FormDialog';
import { useNotify } from '@/hooks/useNotify';
import { toCreditNoteLineInput, useCreateCreditNote, type CreditNoteLineInput, type InvoiceDetail, type InvoiceLineItem } from '@/hooks/useInvoices';
import { useInvoiceConsolidation } from '@/hooks/useConsolidatedInvoices';
import { translateBillingError } from '@/lib/billingErrors';
import {
  readConsolidationSnapshot,
  readLineBookingId,
  resolveConsolidationView,
  workOrdersForCredit,
} from '@/components/workorders/consolidation/presentation';


interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired after the credit note is stored, so the work order can be refetched (#4133). */
  onCreated?: () => void;
  invoice: InvoiceDetail;
}

function creditLinePayload(
  line: InvoiceLineItem,
  bookingId: string | null,
): CreditNoteLineInput & { bookingId?: string } {
  const base = toCreditNoteLineInput(line);
  if (!bookingId) return base;
  return { ...base, bookingId };
}

function workOrderChoiceLabel(workOrder: {
  workOrderNumber: string | null;
  registrationNumber: string | null;
  bookingId: string;
}): string {
  const parts = [workOrder.workOrderNumber, workOrder.registrationNumber].filter(
    (part): part is string => part !== null && part.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' · ') : workOrder.bookingId;
}

export function CreditNoteDialog({ open, onClose, onCreated, invoice }: Props) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const createCreditNote = useCreateCreditNote();
  const consolidation = useInvoiceConsolidation(invoice.id);
  const presentation = resolveConsolidationView({
    status: invoice.status,
    live: consolidation.data ?? null,
    snapshot: readConsolidationSnapshot(invoice),
  });
  const workOrders = workOrdersForCredit(presentation?.workOrders ?? []);

  // Credit only positive-amount lines of the original (skip any discount rows).
  const creditableLines = useMemo(
    () => invoice.lineItems.filter((li) => li.lineTotalCents > 0),
    [invoice.lineItems],
  );
  const [scope, setScope] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(creditableLines.map((l) => l.id)));
  const [reason, setReason] = useState('');

  const scopedLines = scope === 'all'
    ? creditableLines
    : creditableLines.filter((line) => readLineBookingId(line) === scope);

  const applyScope = (nextScope: string) => {
    setScope(nextScope);
    const lines = nextScope === 'all'
      ? creditableLines
      : creditableLines.filter((line) => readLineBookingId(line) === nextScope);
    setSelected(new Set(lines.map((line) => line.id)));
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedLines = scopedLines.filter((l) => selected.has(l.id));
  // Hyvitettävä summa näytetään verollisena — se on se raha, jonka asiakas saa
  // takaisin. Palvelimelle lähtevät rivit pysyvät nettona (docs/VAT-BASIS.md § 5).
  const creditTotalCents = selectedLines.reduce(
    (sum, l) => sum + displayLineGrossCents(l, invoice.taxPercent),
    0,
  );

  const handleSubmit = async () => {
    const forcedBookingId = scope === 'all' ? null : scope;
    const lines = selectedLines.map((line) =>
      creditLinePayload(line, forcedBookingId ?? readLineBookingId(line)),
    );
    try {
      const result = await createCreditNote.mutateAsync({
        invoiceId: invoice.id,
        ...(invoice.bookingId ? { workOrderId: invoice.bookingId } : {}),
        lines,
        ...(reason ? { reason } : {}),
      });
      notify.success(t('credit.success', { number: result.creditNoteNumber }));
      onCreated?.();
      onClose();
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('credit.title')}
      isDirty
      headerIcon={<ReceiptLongIcon />}
      onSubmit={() => void handleSubmit()}
      submitLabel={t('credit.submit')}
      isLoading={createCreditNote.isPending}
      submitDisabled={selectedLines.length === 0}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('credit.help')}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="credit-recipient-change-help">
            {t('credit.recipientChangeHelp')}
          </Typography>
          {workOrders.length > 0 ? (
            <FormControl data-testid="credit-scope">
              <FormLabel>{t('credit.scopeLabel')}</FormLabel>
              <RadioGroup value={scope} onChange={(event) => applyScope(event.target.value)}>
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label={t('credit.scopeInvoice')}
                />
                {workOrders.map((workOrder) => (
                  <FormControlLabel
                    key={workOrder.bookingId}
                    value={workOrder.bookingId}
                    control={<Radio size="small" />}
                    label={workOrderChoiceLabel(workOrder)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t('print.description')}</TableCell>
                  <TableCell align="right">{t('edit.lineTotalInclVat')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scopedLines.map((li) => (
                  <TableRow key={li.id} data-testid="credit-line-row">
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(li.id)}
                        onChange={() => toggle(li.id)}
                        inputProps={{ 'aria-label': li.description }}
                      />
                    </TableCell>
                    <TableCell>{li.description}</TableCell>
                    <TableCell align="right">
                      {formatPrice(displayLineGrossCents(li, invoice.taxPercent))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TextField
            label={t('credit.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            rows={2}
          />

          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={700}>{t('credit.creditTotal')}</Typography>
            <Typography variant="body2" fontWeight={700} color="error.main">
              -{formatPrice(creditTotalCents)}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
